import type { QueryKey } from '@tanstack/react-query'
import type { DashboardOverview, DiagramSource, DiagramSummary, FolderNode } from '../../domains/dashboard/models'
import type { FolderChildren, FolderData } from '../../hooks/useFolderData'
import type { Role, ViewDefinition } from '../../core/repository/DiagramRepository'

export type KnownDiagram = {
  diagram: DiagramSummary
  folder: FolderNode | null
  archived: boolean
}

type FolderDataCache = Partial<FolderData>

type FolderDiagramRecord = {
  id: string
  name: string
  folderId: string
  orgId: string
  type: string
  isTemplate?: boolean
  icon?: string | null
  source?: DiagramSource
  role?: Role
  thumbnailUrl?: string | null
  createdAt: string
  updatedAt: string
  viewDefinition?: ViewDefinition | null
}

type FolderDiagramsCache = {
  diagrams?: FolderDiagramRecord[]
}

const collectRoots = (overview: DashboardOverview): { all: FolderNode[]; archived: FolderNode[] } => {
  const externalOrgRoots = (overview.externalOrgs || []).flatMap((org) => org.roots)
  return {
    all: [
      ...overview.personal.roots,
      ...overview.org.roots,
      ...overview.sharedWithMe.roots,
      ...overview.archived.roots,
      ...externalOrgRoots,
    ],
    archived: overview.archived.roots,
  }
}

const findFolderById = (roots: FolderNode[], id: string): FolderNode | null => {
  const stack = [...roots]
  while (stack.length) {
    const node = stack.pop()!
    if (node.id === id) return node
    if (node.children?.length) stack.push(...node.children)
  }
  return null
}

const findDiagramInTree = (
  roots: FolderNode[],
  diagramId: string,
): { diagram: DiagramSummary; folder: FolderNode } | null => {
  const stack = [...roots]
  while (stack.length) {
    const node = stack.pop()!
    const diagram = node.diagrams?.find((item) => item.id === diagramId)
    if (diagram) return { diagram, folder: node }
    if (node.children?.length) stack.push(...node.children)
  }
  return null
}

const toDiagramSummary = (diagram: FolderDiagramRecord, fallbackSource: DiagramSource): DiagramSummary => ({
  id: diagram.id,
  name: diagram.name,
  folderId: diagram.folderId,
  orgId: diagram.orgId,
  type: diagram.type,
  isTemplate: diagram.isTemplate,
  icon: diagram.icon ?? null,
  source: diagram.source ?? fallbackSource,
  role: diagram.role,
  thumbnailUrl: diagram.thumbnailUrl ?? null,
  createdAt: diagram.createdAt,
  updatedAt: diagram.updatedAt,
  viewDefinition: diagram.viewDefinition ?? null,
})

const isArchivedFolder = (
  folderId: string,
  folder: FolderNode | null,
  archivedRoots: FolderNode[],
  folderDataById: Map<string, FolderDataCache>,
): boolean => {
  if (folder?.archived) return true
  if (findFolderById(archivedRoots, folderId)) return true
  return folderDataById.get(folderId)?.folder?.archived === true
}

export const findKnownDiagram = (
  diagramId: string,
  overview: DashboardOverview | null | undefined,
  folderQueries: ReadonlyArray<readonly [QueryKey, unknown]>,
): KnownDiagram | null => {
  const roots = overview ? collectRoots(overview) : { all: [] as FolderNode[], archived: [] as FolderNode[] }
  const folderDataById = new Map<string, FolderDataCache>()
  for (const [queryKey, value] of folderQueries) {
    if (queryKey[0] !== 'folder' || queryKey[2] !== 'data' || !value) continue
    folderDataById.set(String(queryKey[1]), value as FolderDataCache)
  }
  const resolveFolder = (folderId: string): FolderNode | null => {
    const treeFolder = findFolderById(roots.all, folderId)
    const cached = folderDataById.get(folderId)?.folder
    if (treeFolder) {
      return treeFolder.domain || !cached?.domain
        ? treeFolder
        : { ...treeFolder, domain: cached.domain }
    }
    return cached?.id === folderId ? { ...cached, diagrams: [], children: [] } : null
  }

  if (overview) {
    const inArchivedTree = findDiagramInTree(roots.archived, diagramId)
    if (inArchivedTree) {
      return { diagram: inArchivedTree.diagram, folder: inArchivedTree.folder, archived: true }
    }

    const inTree = findDiagramInTree(roots.all, diagramId)
    if (inTree) {
      return {
        diagram: inTree.diagram,
        folder: resolveFolder(inTree.folder.id),
        archived: isArchivedFolder(inTree.folder.id, inTree.folder, roots.archived, folderDataById),
      }
    }

    const recent = overview.recent.find((diagram) => diagram.id === diagramId)
    if (recent) {
      const folder = resolveFolder(recent.folderId)
      return {
        diagram: recent,
        folder,
        archived: isArchivedFolder(recent.folderId, folder, roots.archived, folderDataById),
      }
    }
  }

  for (const [queryKey, value] of folderQueries) {
    if (queryKey[0] !== 'folder' || !value) continue
    const kind = queryKey[2]
    const folderId = typeof queryKey[1] === 'string' ? queryKey[1] : ''
    if (!folderId) continue

    let records: FolderDiagramRecord[] | undefined
    if (kind === 'children') {
      records = (value as FolderChildren).diagrams
    } else if (kind === 'diagrams') {
      records = (value as FolderDiagramsCache).diagrams
    }
    const match = records?.find((diagram) => diagram.id === diagramId)
    if (!match) continue

    const folder = resolveFolder(match.folderId) ?? resolveFolder(folderId)
    const fallbackSource = folder?.source ?? folderDataById.get(folderId)?.folder?.source ?? 'native'
    return {
      diagram: toDiagramSummary(match, fallbackSource),
      folder,
      archived: isArchivedFolder(folderId, folder, roots.archived, folderDataById)
        || isArchivedFolder(match.folderId, findFolderById(roots.all, match.folderId), roots.archived, folderDataById),
    }
  }

  return null
}
